import { URL } from "../../utils/UrlConstants";
import { privateFetch } from "../../utils/CustomFetch.js";
import { CONSTANT } from "../../utils/Constants";

export interface AdministrativePersonPosition {
  positionId: number;
  positionName: string;
  salaryPerHour: number;
}

export interface AdministrativePerson {
  personId: number;
  fullName: string;
  positions: AdministrativePersonPosition[];
}

export interface AdministrativeSalaryDay {
  id?: number | null;
  dayDate: string; // YYYY-MM-DD
  baseHours: number;
  extraHours: number;
  vacationHours: number;
  deductionHours: number;
  holiday?: boolean;
  /**
   * El colaborador está de vacaciones ese día: horas base en 0 y la jornada
   * completa en horas de vacaciones. Lo decide el backend a partir del registro
   * de vacaciones; la pantalla sólo lo pinta y bloquea la edición.
   */
  vacation?: boolean;
}

export interface AdministrativeSalarySummary {
  workedDays: number;
  grossSalary: number;
  extraHoursAmount: number;
  extraHoursCount: number;
  deductionHoursAmount: number;
  deductionHoursCount: number;
  ccssAmount: number;
  netSalary: number;
}

export interface AdministrativeSalaryCalculation {
  personId: number;
  personName: string;
  positionId: number;
  positionName: string;
  year: number;
  month: number;
  week: number | null;
  /** Hay registros guardados para el período (avances o cierre). */
  saved: boolean;
  /** El período fue cerrado explícitamente — solo lectura. */
  closed: boolean;
  salaryPerHour: number;
  grossMonthlySalary: number;
  netMonthlySalary: number;
  grossBiweeklySalary: number;
  netBiweeklySalary: number;
  dailySalary: number;
  netSalaryPerHour: number;
  netSalaryPerExtraHour: number;
  days: AdministrativeSalaryDay[];
  summary: AdministrativeSalarySummary;
}

export interface PagedAdministrativeSalaryHistory {
  content: AdministrativeSalaryHistoryItem[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface AdministrativeSalaryHistoryItem {
  personId: number;
  personName: string;
  positionId: number;
  positionName: string;
  baseSalary: number;
  baseHours: number;
  extraHours: number;
  vacationHours: number;
  deductionHours: number;
  workedDays: number;
  grossSalary: number;
  deductionsAmount: number;
  netSalary: number;
}

export interface AdministrativeSalaryTotals {
  totalCollaborators: number;
  totalGross: number;
  totalDeductions: number;
  totalNet: number;
}

export interface AdministrativeSalarySavePayload {
  personId: number;
  positionId: number;
  year: number;
  month: number;
  user: string;
  salaryPerHour: number;
  days: AdministrativeSalaryDay[];
  /** true = "Cerrar control" (deja el período en solo lectura); false = "Guardar" avances. */
  close: boolean;
}

export const getAdministrativePersons = async (): Promise<
  AdministrativePerson[]
> => {
  try {
    const response = await privateFetch.get(URL.ADMINISTRATIVE_SALARY_ADMINS);
    return response.data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getAdministrativeSalaryCalculation = async (
  personId: number,
  year: number,
  month: number,
  week?: number,
  positionId?: number
): Promise<AdministrativeSalaryCalculation> => {
  try {
    const params: Record<string, string | number> = {
      personId,
      year,
      month,
    };
    if (week !== undefined && week !== null) {
      params.week = week;
    }
    if (positionId !== undefined && positionId !== null) {
      params.positionId = positionId;
    }
    const response = await privateFetch.get(
      URL.ADMINISTRATIVE_SALARY_CALCULATION,
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const saveAdministrativeSalary = async (
  payload: AdministrativeSalarySavePayload
): Promise<void> => {
  try {
    await privateFetch.post(
      URL.ADMINISTRATIVE_SALARY_SAVE,
      JSON.stringify(payload),
      { headers: { ...CONSTANT.HEADER_TYPE } }
    );
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message
        ? error.response.data.message
        : "No se pudo guardar el cálculo del salario"
    );
  }
};

export const deleteAdministrativeSalary = async (
  personId: number,
  positionId: number,
  year: number,
  month: number
): Promise<void> => {
  try {
    await privateFetch.delete(
      URL.ADMINISTRATIVE_SALARY_DELETE(personId, positionId, year, month)
    );
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message
        ? error.response.data.message
        : "No se pudo eliminar el registro"
    );
  }
};

export const getAdministrativeSalaryHistoryPaged = async (
  year: number,
  month: number,
  page: number,
  size: number,
  week?: number,
  filter?: string
): Promise<PagedAdministrativeSalaryHistory> => {
  try {
    const params: Record<string, string | number> = { year, month, page, size };
    if (week !== undefined && week !== null && week > 0) {
      params.week = week;
    }
    if (filter && filter.trim().length > 0) {
      params.filter = filter.trim();
    }
    const response = await privateFetch.get(
      URL.ADMINISTRATIVE_SALARY_HISTORY_PAGED,
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getAdministrativeSalaryHistory = async (
  year: number,
  month: number,
  week?: number,
  filter?: string
): Promise<AdministrativeSalaryHistoryItem[]> => {
  try {
    const params: Record<string, string | number> = { year, month };
    if (week !== undefined && week !== null && week > 0) {
      params.week = week;
    }
    if (filter && filter.trim().length > 0) {
      params.filter = filter.trim();
    }
    const response = await privateFetch.get(
      URL.ADMINISTRATIVE_SALARY_HISTORY,
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getAdministrativeSalaryTotals = async (
  year: number,
  month: number,
  week?: number,
  filter?: string
): Promise<AdministrativeSalaryTotals> => {
  try {
    const params: Record<string, string | number> = { year, month };
    if (week !== undefined && week !== null && week > 0) {
      params.week = week;
    }
    if (filter && filter.trim().length > 0) {
      params.filter = filter.trim();
    }
    const response = await privateFetch.get(
      URL.ADMINISTRATIVE_SALARY_TOTALS,
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error);
  }
};

const downloadBlob = (data: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const buildExportFileName = (
  prefix: string,
  year: number,
  month: number,
  week: number | undefined,
  ext: string
) =>
  `${prefix}-${year}-${String(month).padStart(2, "0")}${
    week && week > 0 ? `-semana${week}` : ""
  }.${ext}`;

export const exportAdministrativeSalaryExcel = async (
  year: number,
  month: number,
  week?: number,
  filter?: string,
  personIds?: number[]
): Promise<void> => {
  const params: Record<string, any> = { year, month };
  if (week && week > 0) params.week = week;
  if (filter && filter.trim().length > 0) params.filter = filter.trim();
  if (personIds && personIds.length > 0) params.personIds = personIds.join(",");
  const response = await privateFetch.get(
    URL.ADMINISTRATIVE_SALARY_EXPORT_EXCEL,
    { params, responseType: "blob" }
  );
  downloadBlob(
    new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    buildExportFileName("salarios-administrativos", year, month, week, "xlsx")
  );
};

export const exportAdministrativeSalaryPdf = async (
  year: number,
  month: number,
  week?: number,
  filter?: string,
  personIds?: number[]
): Promise<void> => {
  const params: Record<string, any> = { year, month };
  if (week && week > 0) params.week = week;
  if (filter && filter.trim().length > 0) params.filter = filter.trim();
  if (personIds && personIds.length > 0) params.personIds = personIds.join(",");
  const response = await privateFetch.get(
    URL.ADMINISTRATIVE_SALARY_EXPORT_PDF,
    { params, responseType: "blob" }
  );
  downloadBlob(
    new Blob([response.data], { type: "application/pdf" }),
    buildExportFileName("salarios-administrativos", year, month, week, "pdf")
  );
};
